<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    evitar-banner-mobile
    @lang-change="onLangChange"
  >
    <PageHero
      :logo-src="logoBranco"
      logo-alt="OligoCheck"
      :badge="t.heroBadge"
      badge-variante="azul"
      :subtitulo="t.heroSubtitulo"
    >
      <template #titulo>
        <span class="titulo-resto">{{ t.heroTituloAntes + ' ' }}</span
        ><span class="ds-texto-gradiente">{{ t.heroTituloDestaque }}</span>
      </template>

      <template #ctas>
        <!-- Vídeo de interpretação + estudo de caso -->
        <div class="video-wrapper" aria-label="OligoCheck — interpretación y caso real">
          <iframe
            src="https://www.youtube.com/embed/yBVKrDSyCzs?autoplay=0&controls=1&rel=0"
            title="OligoCheck — interpretación y caso real"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>

        <!-- Selo de confiança (acima do CTA) -->
        <p class="selo-seguro">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>{{ t.seloSeguro }}</span>
        </p>

        <!-- Área de CTA: aguardando → libera o botão após o vídeo -->
        <div class="cta-area">
          <transition name="cta-reveal" mode="out-in">
            <!-- Estado 1: aguardando o tempo previsto do vídeo -->
            <div v-if="!mostrarBotao" key="aguardando" class="aguardando">
              <div class="progresso-bar" role="progressbar" aria-label="Progreso del video">
                <div
                  class="progresso-preencher"
                  :style="{ animationDuration: TEMPO_ESPERA_S + 's' }"
                ></div>
              </div>
              <p class="aguardando-texto">
                <span class="aguardando-spinner" aria-hidden="true"></span>
                {{ t.aguardandoTexto }}
              </p>
            </div>

            <!-- Estado 2: CTA revelado -->
            <a
              v-else
              :href="whatsappUrl"
              target="_blank"
              rel="noopener"
              class="caminho-card caminho-compra"
              key="cta"
            >
              <div class="caminho-icone icone-compra">
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
              </div>
              <div class="caminho-conteudo">
                <span class="caminho-label">{{ t.ctaLabel }}</span>
                <strong class="caminho-titulo">{{ t.ctaTitulo }}</strong>
                <span class="caminho-desc">{{ t.ctaDesc }}</span>
              </div>
              <div class="caminho-seta">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </div>
            </a>
          </transition>
        </div>
      </template>
    </PageHero>

    <template #rodape>
      <RodapeSite marca="OligoCheck" />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import PageHero from '@/components/global/layout/PageHero.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import logoBranco from '@/assets/images/logo-branco.svg'
import { useSeo } from '@/composables/useSeo'

const currentLang = ref('es')

const onLangChange = () => {
  /* AppShell já controla a escolha manual internamente. */
}

/* ============================================================
   CTA protegido por tempo
   - Antes de revelar o botão de compra, aguardamos um tempo
     equivalente ao vídeo essencial (anti-skip).
   - Alterar TEMPO_ESPERA_S se a duração do vídeo mudar.
   ============================================================ */
const TEMPO_ESPERA_S = 50
const mostrarBotao = ref(false)
let timer = null

onMounted(() => {
  timer = window.setTimeout(() => {
    mostrarBotao.value = true
  }, TEMPO_ESPERA_S * 1000)
})

onUnmounted(() => {
  if (timer) window.clearTimeout(timer)
})

/* ============================================================
   WhatsApp — mensagem contextualizada ao conteúdo assistido
   ============================================================ */
const whatsappFone = '34617488404'
const whatsappUrl = computed(() => {
  const msgs = {
    es: 'Hola! Me gustaría tener más información sobre el OligoCheck.',
    pt: 'Olá! Gostaria de ter mais informações sobre o OligoCheck.',
    en: "Hi! I'd like to get more information about OligoCheck."
  }
  const msg = encodeURIComponent(msgs[currentLang.value])
  return `https://api.whatsapp.com/send/?phone=${whatsappFone}&text=${msg}`
})

/* ============================================================
   i18n — textos por idioma
   ============================================================ */
const translations = {
  es: {
    heroBadge: 'Contenido exclusivo · OligoCheck',
    heroTituloAntes: 'Interpretación OligoCheck',
    heroTituloDestaque: '+ Estudio de caso',
    heroSubtitulo:
      'Aprende a leer los resultados del análisis mineral intracelular y acompaña un caso real analizado con el OligoCheck.',
    aguardandoTexto: 'Preparando el siguiente paso. Mira el video…',
    ctaLabel: 'Siguiente paso',
    ctaTitulo: 'Quiero adquirir OligoCheck',
    ctaDesc: 'Habla con un consultor por WhatsApp y recibe una propuesta personalizada.',
    seloSeguro: 'Contenido exclusivo y seguro'
  },
  pt: {
    heroBadge: 'Conteúdo exclusivo · OligoCheck',
    heroTituloAntes: 'Interpretação OligoCheck',
    heroTituloDestaque: '+ Estudo de caso',
    heroSubtitulo:
      'Aprenda a ler os resultados da análise mineral intracelular e acompanhe um caso real analisado com o OligoCheck.',
    aguardandoTexto: 'Preparando o próximo passo. Assista ao vídeo…',
    ctaLabel: 'Próximo passo',
    ctaTitulo: 'Quero adquirir o OligoCheck',
    ctaDesc: 'Fale com um consultor pelo WhatsApp e receba uma proposta personalizada.',
    seloSeguro: 'Conteúdo exclusivo e seguro'
  },
  en: {
    heroBadge: 'Exclusive content · OligoCheck',
    heroTituloAntes: 'OligoCheck',
    heroTituloDestaque: 'interpretation + case study',
    heroSubtitulo:
      'Learn how to read the intracellular mineral analysis results and follow a real case analyzed with the OligoCheck device.',
    aguardandoTexto: 'Preparing the next step. Watch the video…',
    ctaLabel: 'Next step',
    ctaTitulo: 'I want to get OligoCheck',
    ctaDesc: 'Talk to a consultant on WhatsApp and receive a personalized offer.',
    seloSeguro: 'Exclusive and secure content'
  }
}

const t = computed(() => translations[currentLang.value])

/* ============================================================
   SEO — página indexável focada em long-tail educacional
   ("interpretación OligoCheck", "interpretação OligoCheck",
    "estudio de caso análisis mineral intracelular").

   Inclui:
     · meta básica multilíngue (title, description, keywords)
     · hreflang (x-default / ES / PT / EN)
     · Open Graph + Twitter Card (gerados pelo composable)
     · JSON-LD:
         - Organization + WebSite (grafo global)
         - WebPage ancorada na Organization
         - VideoObject (enable rich snippet de vídeo)
         - BreadcrumbList
   ============================================================ */
const SITE_URL = 'https://portal.oligocheck.es'
const PAGE_PATH = '/contenido'
const OG_IMAGE =
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/6dce6cd1afe525bd7b8999-logo%20(1).svg'

const VIDEO_ID = 'yBVKrDSyCzs'
const VIDEO_PUBLISH_DATE = '2024-01-15'

const seoContent = {
  es: {
    title: 'Interpretación OligoCheck + Estudio de caso real | Análisis mineral intracelular',
    description:
      'Aprende a interpretar los resultados del análisis mineral intracelular con OligoCheck y sigue un caso clínico real paso a paso en video.',
    keywords: [
      'interpretación oligocheck',
      'cómo leer oligocheck',
      'cómo interpretar oligocheck',
      'estudio de caso oligocheck',
      'caso clínico análisis mineral intracelular',
      'resultado oligocheck',
      'análisis mineral intracelular interpretación',
      'oligocheck resultados explicación',
      'análisis mineral caso real'
    ],
    videoName: 'OligoCheck — Interpretación y estudio de caso',
    videoDesc:
      'Video explicativo sobre cómo interpretar los resultados del análisis mineral intracelular con OligoCheck, con un estudio de caso real paso a paso.',
    breadcrumbHome: 'Inicio',
    breadcrumbPage: 'Contenido OligoCheck',
    ogImageAlt: 'OligoCheck — interpretación y estudio de caso'
  },
  pt: {
    title: 'Interpretação OligoCheck + Estudo de caso real | Análise mineral intracelular',
    description:
      'Aprenda a interpretar os resultados da análise mineral intracelular com o OligoCheck e acompanhe um caso clínico real passo a passo em vídeo.',
    keywords: [
      'interpretação oligocheck',
      'como ler oligocheck',
      'como interpretar oligocheck',
      'estudo de caso oligocheck',
      'caso clínico análise mineral intracelular',
      'resultado oligocheck',
      'análise mineral intracelular interpretação',
      'oligocheck resultados explicação',
      'análise mineral caso real'
    ],
    videoName: 'OligoCheck — Interpretação e estudo de caso',
    videoDesc:
      'Vídeo explicativo sobre como interpretar os resultados da análise mineral intracelular com o OligoCheck, com um estudo de caso real passo a passo.',
    breadcrumbHome: 'Início',
    breadcrumbPage: 'Conteúdo OligoCheck',
    ogImageAlt: 'OligoCheck — interpretação e estudo de caso'
  },
  en: {
    title: 'OligoCheck Interpretation + Real Case Study | Intracellular mineral analysis',
    description:
      'Learn how to interpret intracellular mineral analysis results with OligoCheck, and follow a real clinical case step by step on video.',
    keywords: [
      'oligocheck interpretation',
      'how to read oligocheck',
      'how to interpret oligocheck',
      'oligocheck case study',
      'intracellular mineral analysis case study',
      'oligocheck results',
      'intracellular mineral analysis interpretation',
      'oligocheck results explained',
      'mineral analysis real case'
    ],
    videoName: 'OligoCheck — Interpretation and case study',
    videoDesc:
      'Explainer video on how to interpret the intracellular mineral analysis results with OligoCheck, with a real clinical case study step by step.',
    breadcrumbHome: 'Home',
    breadcrumbPage: 'OligoCheck Content',
    ogImageAlt: 'OligoCheck — interpretation and case study'
  }
}

const canonicalPorLang = {
  es: `${SITE_URL}${PAGE_PATH}`,
  pt: `${SITE_URL}${PAGE_PATH}?lang=pt`,
  en: `${SITE_URL}${PAGE_PATH}?lang=en`
}

useSeo(() => {
  const lang = currentLang.value
  const seo = seoContent[lang] || seoContent.es
  const translation = translations[lang] || translations.es
  const canonical = canonicalPorLang[lang] || canonicalPorLang.es

  const videoObject = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${canonical}#video`,
    name: seo.videoName,
    description: seo.videoDesc,
    thumbnailUrl: [
      `https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`
    ],
    uploadDate: VIDEO_PUBLISH_DATE,
    contentUrl: `https://www.youtube.com/watch?v=${VIDEO_ID}`,
    embedUrl: `https://www.youtube.com/embed/${VIDEO_ID}`,
    publisher: { '@id': `${SITE_URL}#organization` },
    inLanguage: lang,
    isFamilyFriendly: true,
    educationalLevel: 'professional',
    about: {
      '@type': 'Thing',
      name: 'Intracellular mineral analysis',
      sameAs: 'https://en.wikipedia.org/wiki/Spectrophotometry'
    }
  }

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}#organization`,
      name: 'OligoCheck',
      url: SITE_URL,
      logo: OG_IMAGE,
      sameAs: ['https://www.instagram.com/oligocheck/']
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}#website`,
      url: SITE_URL,
      name: 'OligoCheck',
      inLanguage: ['es', 'pt', 'en'],
      publisher: { '@id': `${SITE_URL}#organization` }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: seo.title,
      description: seo.description,
      inLanguage: lang,
      isPartOf: { '@id': `${SITE_URL}#website` },
      primaryImageOfPage: { '@type': 'ImageObject', url: OG_IMAGE },
      video: { '@id': `${canonical}#video` }
    },
    videoObject,
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: seo.breadcrumbHome, item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: seo.breadcrumbPage, item: canonical }
      ]
    }
  ]

  return {
    lang,
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical,
    alternates: {
      'x-default': canonicalPorLang.es,
      es: canonicalPorLang.es,
      pt: canonicalPorLang.pt,
      en: canonicalPorLang.en
    },
    image: OG_IMAGE,
    imageAlt: seo.ogImageAlt,
    type: 'article',
    structuredData
  }
})
</script>

<style scoped>
/* ============================================================
   TÍTULO — texto antes do gradiente
   ============================================================ */
.titulo-resto { color: var(--ds-text); }

/* ============================================================
   VÍDEO
   ============================================================ */
.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 820px;
  aspect-ratio: 16 / 9;
  margin: 0 auto var(--ds-s-7);
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(20, 203, 240, 0.1);
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ============================================================
   ÁREA DE CTA
   ============================================================ */
.cta-area {
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  /* Reserva espaço mínimo para evitar pulo de layout na transição. */
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ---------- Estado 1: aguardando ---------- */
.aguardando {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ds-s-3);
}

.progresso-bar {
  width: 100%;
  max-width: 320px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--ds-border-subtle);
  overflow: hidden;
  position: relative;
}

.progresso-preencher {
  position: absolute;
  inset: 0;
  width: 100%;
  transform-origin: left center;
  background: linear-gradient(90deg, #14cbf0, #1aaf95);
  animation-name: progressoFill;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
}

@keyframes progressoFill {
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
}

.aguardando-texto {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: var(--ds-text-dim);
  font-size: 13px;
  letter-spacing: 0.3px;
  animation: aguardandoPulse 2.2s ease-in-out infinite;
}

@keyframes aguardandoPulse {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}

.aguardando-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(20, 203, 240, 0.18);
  border-top-color: var(--ds-accent);
  animation: aguardandoSpin 0.9s linear infinite;
  flex-shrink: 0;
}

@keyframes aguardandoSpin {
  to { transform: rotate(360deg); }
}

/* ---------- Estado 2: CTA final (caminho-card) ---------- */
.caminho-card {
  display: flex;
  align-items: center;
  gap: var(--ds-s-4);
  text-align: left;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 22px;
  text-decoration: none;
  color: var(--ds-text);
  transition: all var(--ds-t-smooth);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.caminho-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--ds-t-base);
  pointer-events: none;
  background: linear-gradient(135deg, rgba(245, 130, 32, 0.16), transparent 60%);
}

.caminho-card:hover::before { opacity: 1; }

.caminho-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--ds-shadow-lg);
  border-color: rgba(245, 130, 32, 0.45);
}

.caminho-icone {
  min-width: 56px;
  height: 56px;
  border-radius: var(--ds-r-md);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  color: #fff;
}

.icone-compra {
  background: var(--ds-gradient-warning);
  box-shadow: var(--ds-shadow-glow-warning);
}

.caminho-conteudo {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  position: relative;
  z-index: 1;
  min-width: 0;
}

.caminho-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-bold);
}

.caminho-titulo {
  font-family: var(--ds-font-bold);
  font-size: 17px;
  color: var(--ds-text);
  line-height: 1.25;
}

.caminho-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.45;
  margin-top: 2px;
}

.caminho-seta {
  color: var(--ds-text-faint);
  transition: all var(--ds-t-base);
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.caminho-card:hover .caminho-seta {
  color: var(--ds-text);
  transform: translateX(4px);
}

/* ============================================================
   TRANSIÇÃO entre estado "aguardando" e "CTA"
   ============================================================ */
.cta-reveal-enter-active {
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}
.cta-reveal-leave-active {
  transition: all 0.3s ease-in;
}
.cta-reveal-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}
.cta-reveal-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ============================================================
   SELO
   ============================================================ */
.selo-seguro {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto var(--ds-s-7);
  padding: 8px 14px;
  border: 1px solid var(--ds-border-subtle);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.02);
  color: var(--ds-text-faint);
  font-size: 12px;
  letter-spacing: 0.3px;
  width: fit-content;
  max-width: 100%;
}

.selo-seguro svg { flex-shrink: 0; }

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 720px) {
  .video-wrapper {
    border-radius: var(--ds-r-lg);
    margin-bottom: var(--ds-s-6);
  }

  .cta-area { min-height: 104px; }

  .caminho-card { padding: 16px; gap: var(--ds-s-3); }
  .caminho-icone { min-width: 48px; height: 48px; }
  .caminho-titulo { font-size: 15px; }
  .caminho-desc { font-size: 12.5px; }

  .aguardando-texto { font-size: 12.5px; }
  .selo-seguro { font-size: 11.5px; padding: 7px 12px; }
}
</style>
